<template>
  <div class="container">
    <input
      type="file"
      id="image-input"
      class="image-input"
      :accept="accept"
      :disabled="disabled"
      @change="imageSelected"
    />

    <div class="upload-state">
      <div class="image-preview" :style="{ width: previewWidth + 'px', height: previewHeight + 'px' }">
        <div v-if="imageUploaded" class="image-preview__image checkerboard">
          <img :src="imageUrl" />
        </div>
        <div v-if="!imageUploaded" class="image-preview__placeholder">{{ previewWidth }}px x {{ previewHeight }}px</div>
      </div>
    </div>

    <div class="upload-action">
      <label
        for="image-input"
        v-if="!imageUploaded"
        class="button is-primary"
        :class="{ 'is-loading': uploading }"
        :disabled="disabled"
      >
        Choose file...
      </label>
      <div v-if="imageUploaded" class="selected-image-buttons">
        <n-button variant="primary" @click="clearImage()">Clear</n-button>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { imageUploadApi } from '../services/webAssistantImageUpload';
import Toast from '@/shared/services/toast';
import { ref, watch } from 'vue';

const props = defineProps({
  accept: {
    type: String,
  },
  previewWidth: {
    type: Number,
  },
  previewHeight: {
    type: Number,
  },
  image: {
    type: String,
  },
  disabled: {
    type: Boolean,
  },
});

const imageUploaded = ref(false);
const imageUrl = ref('');
const uploading = ref(false);

let inputElement: HTMLInputElement;

watch(
  () => props.image,
  (value) => {
    if (value) {
      imageUploaded.value = true;
      imageUrl.value = value;
    }
  },
  { immediate: true },
);

const emit = defineEmits<{
  (e: 'selected'): void;
  (e: 'uploaded', url: string): void;
  (e: 'cleared'): void;
}>();

function emitSelected() {
  emit('selected');
}

function emitUploaded(url: string) {
  emit('uploaded', url);
}

function emitCleared() {
  emit('cleared');
}

function imageSelected(e: Event) {
  if (props.disabled) {
    return;
  }

  inputElement = e.target as HTMLInputElement;
  if (!inputElement.files || inputElement.files.length === 0) {
    return;
  }

  const selectedFile = inputElement.files[0];
  if (!isValidFileType(selectedFile.type)) {
    Toast.showError(`Invalid file type - accepted types are .png, .jpg or .jpeg`);
    inputElement.value = '';
    return;
  }

  emitSelected();

  uploading.value = true;

  imageUploadApi
    .upload(selectedFile)
    .then((response) => {
      imageUploaded.value = true;
      imageUrl.value = response.url;
      emitUploaded(response.url);
      Toast.showSuccess('Image uploaded successfully');
    })
    .catch((err) => {
      // clear the file from the input
      // fixes issue where nothing happens if the
      // user goes to select the same image again
      inputElement.value = '';
      Toast.showError(err.message);
      emitCleared();
    })
    .finally(() => {
      uploading.value = false;
    });
}

function clearImage() {
  imageUrl.value = '';
  if (inputElement) {
    inputElement.value = '';
  }
  imageUploaded.value = false;
  emitCleared();
}

function isValidFileType(type: string) {
  return type === 'image/png' || type === 'image/jpeg';
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
.image-input {
  visibility: hidden;
  width: 0;
  height: 0;
  position: absolute;
}

.container {
  display: flex;
  align-items: center;

  .upload-state {
    display: flex;
    flex-direction: column;
  }

  .upload-action {
    margin-left: 1em;
    display: flex;
    align-items: center;
  }
}

.image-preview {
  cursor: default;
  display: flex;

  .image-preview__image {
    overflow: hidden;
    width: 100%;
    height: 100%;

    img {
      max-width: unset;
    }
  }

  .image-preview__placeholder {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    color: #6d6d6d;
    background-color: #eee;
  }
}

.checkerboard {
  $checkerboard_primary: #808080;
  $checkerboard_secondary: #b3b3b3;
  border: 1px solid #808080;

  transition: none;
  background-color: $checkerboard_secondary;
  background-image:
    linear-gradient(45deg, $checkerboard_primary 25%, transparent 25%),
    linear-gradient(-45deg, $checkerboard_primary 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, $checkerboard_primary 75%),
    linear-gradient(-45deg, transparent 75%, $checkerboard_primary 75%);
  background-size: 20px 20px;
  background-position:
    0 0,
    0 10px,
    10px -10px,
    -10px 0px;
}
</style>
